import React, {useContext, useEffect, useState, useMemo} from 'react';
import {styled} from "@mui/material/styles";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import {UserContext} from "../../context/userContext";
import {AccountContext} from "../../context/accountContext";
import { IconButton, Box, Select, MenuItem, FormControl, InputLabel, Dialog, DialogTitle, DialogContent, DialogActions, Fab, Tooltip } from '@mui/material';
import TextField from "@mui/material/TextField";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import Button from "@mui/material/Button";
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import {AuthContext} from "../../context/authContext";
import { HexColorPicker } from "react-colorful";
import formatter from "../../utils/formatter";
import AppDrawer from "../AppDrawer/AppDrawer";
import Typography from "@mui/material/Typography";
import frugalServicesClient from "../../utils/frugal-services-client";

const PREFIX = 'Categories';
const classes = {
   root: `${PREFIX}-root`,
   paper: `${PREFIX}-paper`,
   table: `${PREFIX}-table`,
   header: `${PREFIX}-header`,
   colorBox: `${PREFIX}-colorBox`,
}
const Root = styled('div')(({ theme }) => ({
   [`&.${classes.root}`]: {
    display: "flex",
    paddingTop: '64px'
   },
   [`& .${classes.paper}`]: {
      padding: theme.spacing(2),
      color: theme.palette.text.secondary,
      width: '100%'
   },
   [`& .${classes.table}`]: {
      marginTop: theme.spacing(2),
   },
   [`& .${classes.colorBox}`]: {
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      border: '1px solid #ccc',
   },
   [`& .${classes.header}`]: {
      marginBottom: theme.spacing(1),
   },
}))

export default function Categories(props) {
   const {setPasswordExpired} = useContext(AuthContext);
   const {user} = useContext(UserContext);
   const {accountCategories, setLoadingAccountCategories} = useContext(AccountContext);
   
   const [newCategory, setNewCategory] = useState('');
   const [newCategoryColor, setNewCategoryColor] = useState(formatter.getRandomChartColor());
   const [newCategoryAvailable, setNewCategoryAvailable] = useState(true);
   const [newCategoryLabel, setNewCategoryLabel] = useState('Create category');
   const [newCategoryValid, setNewCategoryValid] = useState(true);
   const [filter, setFilter] = useState('All');
   const [open, setOpen] = useState(false);

   const handleClickOpen = () => {
      setOpen(true);
   };

   const handleClose = () => {
      setOpen(false);
   };

   const handleNewCategoryChange = e => {
      setNewCategory(e.target.value);
   }

   const handleFilterChange = (event) => {
      setFilter(event.target.value);
   };

   const combinedCategories = useMemo(() => {
      const categories = accountCategories.map(cat => ({
         ...cat,
         type: cat.accountId ? 'Custom' : 'Default'
      })).sort((a, b) => a.category.localeCompare(b.category));

      if (filter === 'All') {
         return categories;
      }
      return categories.filter(cat => cat.type === filter);
   }, [accountCategories, filter]);

   useEffect(() => {
      if(newCategory.length > 3) {
         checkIfNewCategoryIsDuplicate();
      }
   }, [newCategory]);

   useEffect(() => {
      if(newCategory.length > 3 || newCategory.length === 0) {
         setNewCategoryValid(true);
      } else {
         setNewCategoryValid(false);
         setNewCategoryLabel("3 character min")
      }
   }, [newCategory]);

   function checkIfNewCategoryIsDuplicate() {
      (async () => {
         const response = await frugalServicesClient.checkCategory(newCategory);
         if(response.status === 401) {
            setPasswordExpired(true);
         }
         const categoryAvailableStatus = await response.json();
         setNewCategoryAvailable(categoryAvailableStatus);
         if(categoryAvailableStatus) {
            setNewCategoryLabel("Create category")
         } else {
            setNewCategoryLabel("Category exists");
         }
      })();
   }

   function createCustomCategory() {
      if (newCategory.length > 3 && newCategoryAvailable) {
         frugalServicesClient.createCategory({
            accountId: user.accountId,
            category: newCategory,
            color: newCategoryColor,
         }).then(function (response) {
            if(response.status === 401) {
               setPasswordExpired(true);
            }
            setLoadingAccountCategories(true);
            setNewCategory('');
            setNewCategoryLabel('Create category');
            setNewCategoryValid(true);
            handleClose();
         }).catch(function (error) {
            console.error('Request failure: ', error);
         });
      }
   }

   function deleteCustomCategory(id) {
      (async () => {
         const response = await frugalServicesClient.deleteCategory(id);
         if(response.status === 401) {
            setPasswordExpired(true);
         }
         setLoadingAccountCategories(true);
      })();
   }

   return (
      <Root className={classes.root}>
        <AppDrawer/>
         <Paper className={classes.paper}>
            <Grid container justifyContent="space-between" alignItems="center" className={classes.header}>
               <Grid>
                  <Typography variant="h4" gutterBottom>Manage Categories</Typography>
               </Grid>
               <Grid>
                  <Tooltip title="Add Category">
                     <Fab color="secondary" aria-label="add" onClick={handleClickOpen}>
                        <AddIcon />
                     </Fab>
                  </Tooltip>
               </Grid>
            </Grid>
            
            <Grid container justifyContent="flex-start" alignItems="center" sx={{ mt: 4,mb: 2 }}>
               <Grid>
                  <FormControl sx={{ minWidth: 200 }}>
                     <InputLabel>Filter by Type</InputLabel>
                     <Select value={filter} onChange={handleFilterChange} label="Filter by Type">
                        <MenuItem value="All">All</MenuItem>
                        <MenuItem value="Custom">Custom</MenuItem>
                        <MenuItem value="Default">Default</MenuItem>
                     </Select>
                  </FormControl>
               </Grid>

            </Grid>

            <TableContainer component={Paper} className={classes.table}>
               <Table sx={{ width: '100%' }} aria-label="categories table">
                  <TableHead>
                     <TableRow>
                        <TableCell style={{ width: '75px'}}></TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell style={{ width: '75px' }}>Actions</TableCell>
                     </TableRow>
                  </TableHead>
                  <TableBody>
                     {combinedCategories.map((category) => (
                     <TableRow key={category.id}>
                        <TableCell>
                           <Box className={classes.colorBox} style={{ backgroundColor: category.color }} />
                        </TableCell>
                        <TableCell>{category.category}</TableCell>
                        <TableCell>{category.type}</TableCell>
                        <TableCell>
                           {category.type === 'Custom' && (
                              <IconButton sx={{ padding: 0 }} aria-label="delete" onClick={() => deleteCustomCategory(category.id)}>
                                 <DeleteIcon />
                              </IconButton>
                           )}
                        </TableCell>
                     </TableRow>
                     ))}
                  </TableBody>
               </Table>
            </TableContainer>
        </Paper>
         <Dialog open={open} onClose={handleClose}>
            <DialogTitle>Create a New Category</DialogTitle>
            <DialogContent>
               <Grid container direction="column" spacing={2} sx={{pt: 1}}>
                  <Grid>
                     <Typography variant="subtitle1">Name</Typography>
                     <TextField fullWidth id="outlined-basic" label={newCategoryLabel} variant="outlined" value={newCategory} onChange={handleNewCategoryChange} error={!newCategoryAvailable || !newCategoryValid}/>
                  </Grid>
                  <Grid>
                     <Typography variant="subtitle1">Category Color</Typography>
                     <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <HexColorPicker color={newCategoryColor} onChange={setNewCategoryColor} />
                     </Box>
                  </Grid>
               </Grid>
            </DialogContent>
            <DialogActions>
               <Button onClick={handleClose}>Cancel</Button>
               <Button onClick={createCustomCategory} color="secondary">Add</Button>
            </DialogActions>
         </Dialog>
      </Root>
   );
}